/** Broad savanna umbrellas: crooked forks support a dense, nearly level roof
 * of overlapping foliage plates. A single botanical plan feeds both LODs. */
import * as THREE from 'three';
import { range, type Rng } from '@hyperlight/shared';
import type { BuildCtx, C3, SpeciesParams } from './types';
import { V, shade } from './primitives';
import { TREE_STEM, NO_JOINT, windJoint, type WindJoint } from './treeWind';

export interface AcaciaKnot { p: THREE.Vector3; radius: number }
export interface AcaciaPlate {
  center: THREE.Vector3; radius: number; depth: number; phase: number;
  stretch: number; lobes: number[]; edgeY: number[]; facetTone: number[]; tone: number;
}
export interface AcaciaBranch { path: AcaciaKnot[]; twigs: AcaciaKnot[][]; plates: AcaciaPlate[] }
export interface AcaciaPlan { stem: AcaciaKnot[]; branches: AcaciaBranch[]; height: number }

export function planAcacia(rng: Rng, sp: SpeciesParams, maturity: number): AcaciaPlan {
  const grow = .45 + .75 * maturity, girth = .65 + .6 * maturity;
  const h = range(rng, ...sp.trunk.height) * grow, radius = sp.trunk.radius * girth;
  const phase = rng() * Math.PI * 2, lean = h * sp.trunk.lean;
  const outward = V(Math.cos(phase), 0, Math.sin(phase)), across = V(-outward.z, 0, outward.x);
  const stem = [
    { p: V(0, -.22, 0), radius: radius * 1.45 },
    { p: outward.clone().multiplyScalar(lean * .08).add(V(0, h * .16, 0)), radius: radius * .93 },
    { p: outward.clone().multiplyScalar(-lean * .30).addScaledVector(across, lean * .18).add(V(0, h * .30, 0)), radius: radius * .76 },
    { p: outward.clone().multiplyScalar(lean * .72).addScaledVector(across, lean * .10).add(V(0, h * sp.trunk.forkHeight, 0)), radius: radius * .58 },
  ];
  const branches: AcaciaBranch[] = [];
  for (let i = 0; i < 3; i++) {
    const angle = phase + [0, 2.5, 4.35][i] + range(rng, -.17, .17);
    const out = V(Math.cos(angle), 0, Math.sin(angle)), side = V(-out.z, 0, out.x);
    const root = stem[2].p.clone().lerp(stem[3].p, [.62, .92, .22][i]);
    const reach = h * (sp.branching?.spread ?? .56) * range(rng, .90, 1.08);
    const elbow = root.clone().addScaledVector(out, reach * .25).addScaledVector(side, range(rng, -.22, .22) * grow); elbow.y = h * .60;
    const fork = root.clone().addScaledVector(out, reach * .63).addScaledVector(side, range(rng, -.30, .30) * grow); fork.y = h * .76;
    const tip = root.clone().addScaledVector(out, reach); tip.y = h * .835;
    const path = [{ p: root, radius: radius * .59 }, { p: elbow, radius: radius * .46 },
      { p: fork, radius: radius * .29 }, { p: tip, radius: radius * .16 }];
    const plates: AcaciaPlate[] = [], twigs: AcaciaKnot[][] = [];
    for (let j = 0; j < 4; j++) {
      const r = range(rng, ...sp.canopy.size) * grow;
      const center = tip.clone().addScaledVector(out, [0, -.55, -.68, -1.13][j] * r)
        .addScaledVector(side, [0, .78, -.76, .08][j] * r);
      // A few broad height steps make overlapping masses legible, while all
      // plates still belong to one shallow umbrella rather than tiered balls.
      center.y = h * (.910 + [.023, -.018, .007][i] + (j === 3 ? .018 : 0) + range(rng, -.023, .026));
      const plate: AcaciaPlate = { center, radius: r * (j === 3 ? .94 : 1), depth: grow * range(rng, 1.10, 1.46),
        phase: angle + range(rng, -.4, .4), stretch: range(rng, .95, 1.18),
        lobes: Array.from({ length: 12 }, (_, k) => range(rng, 1 - sp.canopy.rough * .70, 1 + sp.canopy.rough * .80) * (k % 3 === 1 ? .86 : 1)),
        edgeY: Array.from({ length: 12 }, () => range(rng, -.21, .14) * grow),
        facetTone: Array.from({ length: 12 }, () => range(rng, .89, 1.09)), tone: range(rng, .83, 1.12) };
      const twigRoot = j === 0 ? tip.clone() : j === 3 ? elbow.clone().lerp(fork, .75) : fork.clone();
      const twigTip = center.clone().add(V(0, -plate.depth * .22, 0));
      const knee = twigRoot.clone().lerp(twigTip, .60).addScaledVector(side, (j % 2 ? -.13 : .13) * grow);
      twigs.push([{ p: twigRoot, radius: radius * .16 }, { p: knee, radius: radius * .10 }, { p: twigTip, radius: radius * .045 }]);
      plates.push(plate);
    }
    branches.push({ path, twigs, plates });
  }
  const height = Math.max(...branches.flatMap(b => b.plates.map(p => p.center.y + p.depth * .65)));
  return { stem, branches, height };
}

class AcaciaMesh {
  p: number[] = []; c: number[] = []; sway: number[] = []; branch: number[] = []; twig: number[] = [];
  branchJoint: WindJoint = TREE_STEM; twigJoint: WindJoint = NO_JOINT;
  tri(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: C3, sway = 0): void {
    for (const p of [a, b, c]) { this.p.push(...p.toArray()); this.c.push(...color); this.sway.push(sway);
      this.branch.push(...this.branchJoint); this.twig.push(...this.twigJoint); }
  }
  wood(path: AcaciaKnot[], color: C3, sides: number, caps = true): void {
    const rings = path.map((k, i) => {
      const dir = path[Math.min(i + 1, path.length - 1)].p.clone().sub(path[Math.max(0, i - 1)].p).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), dir);
      return Array.from({ length: sides }, (_, j) => V(Math.cos(j * Math.PI * 2 / sides) * k.radius, 0,
        Math.sin(j * Math.PI * 2 / sides) * k.radius).applyQuaternion(q).add(k.p));
    });
    for (let s = 0; s < sides; s++) {
      const n = (s + 1) % sides;
      if (caps) this.tri(path[0].p, rings[0][s], rings[0][n], color);
      for (let i = 0; i < path.length - 1; i++) {
        const c = shade(color, .90 + .055 * ((s + i) % 4));
        this.tri(rings[i][s], rings[i + 1][s], rings[i][n], c);
        this.tri(rings[i][n], rings[i + 1][s], rings[i + 1][n], c);
      }
      if (caps) this.tri(path.at(-1)!.p, rings.at(-1)![n], rings.at(-1)![s], color);
    }
  }
  /** Closed flattened leaf masses with a low faceted dome and a scalloped
   * underside. Broad overlap supplies the connected umbrella silhouette. */
  plate(p: AcaciaPlate, color: C3, coarse: boolean, sides = coarse ? 6 : 12): void {
    const ring = (size: number, height: number) => Array.from({ length: sides }, (_, i) => {
      const index = Math.floor(i * 12 / sides), angle = p.phase + index * Math.PI / 6;
      const x = Math.cos(angle) * p.radius * p.lobes[index] * size, z = Math.sin(angle) * p.radius * p.lobes[index] * size;
      return p.center.clone().add(V(x * p.stretch, height * p.depth + p.edgeY[index] * size, z));
    });
    const rings = coarse ? [ring(1, 0)] : [ring(.57, -.40), ring(1, 0), ring(.67, .44)];
    const bottom = p.center.clone().add(V(0, -p.depth * .58, 0)), top = p.center.clone().add(V(0, p.depth * .65, 0));
    for (let i = 0; i < sides; i++) {
      const n = (i + 1) % sides;
      const facet = shade(color, p.facetTone[Math.floor(i * 12 / sides)]);
      this.tri(bottom, rings[0][i], rings[0][n], shade(color, .86), 1);
      for (let j = 0; j < rings.length - 1; j++) {
        this.tri(rings[j][i], rings[j + 1][i], rings[j][n], shade(facet, .94 + .04 * j), 1);
        this.tri(rings[j][n], rings[j + 1][i], rings[j + 1][n], shade(facet, .97 + .04 * j), 1);
      }
      this.tri(top, rings.at(-1)![n], rings.at(-1)![i], facet, 1);
    }
  }
  finish(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    for (const [name, data, itemSize] of [['position', this.p, 3], ['color', this.c, 3], ['sway', this.sway, 1],
      ['branchWind', this.branch, 4], ['twigWind', this.twig, 4]] as const) g.setAttribute(name, new THREE.Float32BufferAttribute(data, itemSize));
    g.computeVertexNormals(); return g;
  }
}

export function buildAcacia(rng: Rng, sp: SpeciesParams, ctx: BuildCtx, far: boolean): THREE.BufferGeometry {
  const plan = planAcacia(rng, sp, ctx.maturity), mesh = new AcaciaMesh();
  mesh.wood(plan.stem, sp.palette.bark, far ? 3 : 7);
  for (const branch of plan.branches) {
    const joint = windJoint(branch.path[0].p, branch.path.at(-1)!.p, branch.path[0].radius, .62);
    mesh.branchJoint = joint; mesh.twigJoint = NO_JOINT;
    mesh.wood(branch.path, sp.palette.bark, far ? 3 : 6);
    for (let i = 0; i < branch.plates.length; i++) {
      const twig = branch.twigs[i], plate = branch.plates[i];
      mesh.twigJoint = windJoint(twig[0].p, twig.at(-1)!.p, twig[0].radius, .60);
      mesh.wood(far ? [twig[0], twig.at(-1)!] : twig, sp.palette.bark, far ? 3 : 5, !far);
      mesh.plate(plate, shade(sp.palette.leafSummer, plate.tone), far);
      for (let j = 0; j < (far ? 1 : 2); j++) {
        const angle = plate.phase + j * 2.4 + .5;
        mesh.plate({ ...plate, center: plate.center.clone().add(V(Math.cos(angle) * plate.radius * .72,
          -plate.depth * .22, Math.sin(angle) * plate.radius * .72)), radius: plate.radius * .40,
          depth: plate.depth * .65, stretch: 1 }, shade(sp.palette.leafSummer, plate.tone * (j ? .94 : 1.04)), true, far ? 5 : 6);
      }
    }
  }
  const g = mesh.finish();
  const path = (knots: AcaciaKnot[]) => knots.map(k => ({ p: k.p.toArray(), radius: k.radius }));
  g.userData.acacia = { stem: path(plan.stem), height: plan.height,
    branches: plan.branches.map(b => ({ path: path(b.path), root: b.path[0].p.toArray(), tip: b.path.at(-1)!.p.toArray(),
      joint: windJoint(b.path[0].p, b.path.at(-1)!.p, b.path[0].radius, .62),
      twigs: b.twigs.map(t => ({ path: path(t), root: t[0].p.toArray(), tip: t.at(-1)!.p.toArray(),
        joint: windJoint(t[0].p, t.at(-1)!.p, t[0].radius, .60) })),
      plates: b.plates.map(p => ({ center: p.center.toArray(), radius: p.radius, depth: p.depth, phase: p.phase, stretch: p.stretch })) })) };
  return g;
}
